import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, Observable, take } from 'rxjs';
import { NotebookFacade, NotebookSidebarPreference, NotebookType, SessionResource } from '@mode/notebooks/contract';
import { OrgTypes } from '@mode/shared/contract-common';
import * as LegacyNotebookSelectors from './legacy-notebook.selectors';
import * as NotebookActions from './notebook.actions';
import * as NotebookCellSelectors from '../notebook-cell/legacy-notebook-cell.selectors';

@Injectable()
export class NotebookService implements NotebookFacade {
  public notebookToken$: Observable<string>;
  public notebookType$: Observable<string>;
  public accountPreference$: Observable<any>;
  public accountContextPlanCode$: Observable<OrgTypes.PlanCode>;
  public selectedReportReportRunStateSelector$: Observable<any>;
  public selectedNotebookSession$: Observable<SessionResource>;

  constructor(private store: Store) {
    this.notebookToken$ = this.store.select(LegacyNotebookSelectors.notebookToken);
    this.notebookType$ = this.store.select(LegacyNotebookSelectors.selectedNotebookType);
    this.accountPreference$ = this.store.select(LegacyNotebookSelectors.accountPreferenceSelector);
    this.accountContextPlanCode$ = this.store.select(LegacyNotebookSelectors.accountContextPlanCode);
    this.selectedReportReportRunStateSelector$ = this.store.select(
      LegacyNotebookSelectors.selectedReportReportRunStateSelector
    );
    this.selectedNotebookSession$ = this.store.select(LegacyNotebookSelectors.selectedNotebookSession);
  }

  public updatePreference(preference: NotebookSidebarPreference): void {
    this.store.dispatch(NotebookActions.updatePreference({ params: preference }));
  }

  public updateNotebookType(notebookToken: string, notebookType: NotebookType): void {
    this.store.dispatch(NotebookActions.updateNotebookType({ notebookToken, notebookType }));
  }

  createNotebook(reportToken: string): void {
    return this.store.dispatch(NotebookActions.createNotebook({ reportToken }));
  }

  initSession(notebookToken: string, reuseIfActive: boolean, runNewCells?: boolean): void {
    this.store.dispatch(
      NotebookActions.initSession({
        notebookToken,
        reuseIfActive,
        runNewCells,
      })
    );
  }

  clearAllCellOutputs(notebookToken: string): void {
    this.store.dispatch(
      NotebookActions.clearAllCellOutputs({
        notebookToken,
      })
    );
  }

  cancelRun(notebookToken: string): void {
    this.store.dispatch(NotebookActions.cancelRun({ notebookToken }));
  }

  runNotebook(notebookToken: string, cellTokensToRun?: string[]): void {
    this.store
      .select(NotebookCellSelectors.createRunnableCellTokensSelector(notebookToken))
      .pipe(
        take(1),
        map((tokensSet: any) => {
          const filteredTokens = cellTokensToRun
            ? cellTokensToRun.filter((token) => tokensSet.has(token))
            : Array.from(tokensSet);

          return NotebookActions.run({
            notebookToken,
            runAllCells: cellTokensToRun == null,
            cellTokensToRun: filteredTokens.map((token) => token as string),
          });
        })
      )
      .subscribe((action) => {
        this.store.dispatch(action);
      });
  }

  refreshSessionReportRun(notebookToken: string): void {
    this.store.dispatch(NotebookActions.refreshSessionReportRun({ notebookToken }));
  }

  toggleNotebookSidebar(): void {
    this.store.dispatch(NotebookActions.toggleNotebookSidebar());
  }

  initNotebook(): void {
    this.store.dispatch(NotebookActions.initNotebook());
  }

  addNotebook(reportToken: string): void {
    this.store.dispatch(NotebookActions.addNotebook({ reportToken }));
  }

  importNotebook(file: File, reportToken?: string): void {
    this.store.dispatch(NotebookActions.importNotebook({ file, reportToken }));
  }
}
